/** Dispatched when a text-audio tip opens so playback can pause first. */
export const TEXT_AUDIO_PAUSE_FOR_TIP_EVENT = 'persian-learning:text-audio-pause-for-tip';

/** Delay before outside-click / scroll can dismiss a newly opened tip. */
export const TEXT_AUDIO_TIP_OPEN_GRACE_MS = 700;

export function dispatchPauseTextAudioForTip(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TEXT_AUDIO_PAUSE_FOR_TIP_EVENT));
}

/** While true, TextAudioBlock must not reposition tip icon wrappers. */
export const TEXT_AUDIO_TIP_POINTER_EVENT = 'persian-learning:text-audio-tip-pointer';

export function setTextAudioTipPointerActive(active: boolean): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TEXT_AUDIO_TIP_POINTER_EVENT, { detail: { active } }));
}

/** Survives TipIcon portal remounts while audio pause/layout updates run. */
const openTextAudioTipIds = new Set<string>();

/** Tip was just opened by the learner — play enter animation even if TipIcon remounts. */
const pendingOpenAnimationTipIds = new Set<string>();

export function markTextAudioTipOpen(tipId: string): void {
  openTextAudioTipIds.add(tipId);
}

export function markTextAudioTipPendingOpenAnimation(tipId: string): void {
  pendingOpenAnimationTipIds.add(tipId);
}

export function clearTextAudioTipPendingOpenAnimation(tipId: string): void {
  pendingOpenAnimationTipIds.delete(tipId);
}

export function isTextAudioTipPendingOpenAnimation(tipId: string): boolean {
  return pendingOpenAnimationTipIds.has(tipId);
}

export function markTextAudioTipClosed(tipId: string): void {
  openTextAudioTipIds.delete(tipId);
  pendingOpenAnimationTipIds.delete(tipId);
}

export function isTextAudioTipMarkedOpen(tipId: string): boolean {
  return openTextAudioTipIds.has(tipId);
}

export function isWithinTipOpenGracePeriod(openedAtMs: number): boolean {
  if (!openedAtMs) return false;
  return performance.now() - openedAtMs < TEXT_AUDIO_TIP_OPEN_GRACE_MS;
}

/** @deprecated use isWithinTipOpenGracePeriod */
export function shouldIgnoreTipScrollClose(openedAtMs: number): boolean {
  return isWithinTipOpenGracePeriod(openedAtMs);
}

/** True when the anchor has scrolled fully out of the viewport or its scroll parent. */
export function isAnchorOutsideView(el: HTMLElement): boolean {
  if (!el.isConnected) return true;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) return true;
  if (rect.bottom <= 0 || rect.top >= window.innerHeight || rect.right <= 0 || rect.left >= window.innerWidth) {
    return true;
  }
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const clipsY = style.overflowY === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'hidden';
    const clipsX = style.overflowX === 'auto' || style.overflowX === 'scroll' || style.overflowX === 'hidden';
    if (clipsX || clipsY) {
      const bounds = node.getBoundingClientRect();
      const hiddenY = clipsY && (rect.bottom <= bounds.top || rect.top >= bounds.bottom);
      const hiddenX = clipsX && (rect.right <= bounds.left || rect.left >= bounds.right);
      if (hiddenY || hiddenX) return true;
    }
    node = node.parentElement;
  }
  return false;
}

/** Synchronous tooltip position so the panel is visible on the first tap. */
export function computeTipTooltipPosition(
  icon: HTMLElement,
  size?: { width: number; height: number } | null
): { x: number; y: number } {
  const rect = icon.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const estimatedWidth = size?.width ?? viewportWidth * 0.3;
  const estimatedHeight = size?.height ?? viewportHeight * 0.1;
  const padding = 10;

  let x = rect.left + rect.width / 2 - estimatedWidth / 2;
  let y = rect.top - estimatedHeight - 10;

  if (y < padding) {
    y = rect.bottom + 10;
  }
  if (x + estimatedWidth > viewportWidth - padding) {
    x = viewportWidth - estimatedWidth - padding;
  }
  if (x < padding) {
    x = padding;
  }
  if (y + estimatedHeight > viewportHeight - padding) {
    y = viewportHeight - estimatedHeight - padding;
  }
  if (y < padding) {
    y = padding;
  }

  return { x, y };
}
